import React from 'react';
import ReactTable, { ReactTableDefaults } from 'react-table';
import propTypes from 'prop-types';
import _ from 'lodash';
import { Columns, Column } from 'src/ui/mui-ui-kit';
import { UiPagination } from './UiPagination';
import { InnerLoader, NextButton } from '../common';
import { UiEmptyState } from './UiEmptyState';

const DEFAULT_PAGE_SIZE = 20;

Object.assign(ReactTableDefaults, {
  LoadingComponent: InnerLoader,
  getLoadingProps: state => ({ visible: state.loading }),
  previousText: '◀',
  nextText: '▶',
});

export function UiTable({
  data,
  title,
  emptyTo,
  emptyOnClickButton,
  renderButton,
  paging,
  pageSize,
  useTablePagination,
  hideButton,
  loading,
  toNext,
  onClickNext,
  toPrev,
  onClickPrev,
  hideNext,
  hidePrev,
  hideNoDataComponent,
  minRows,
  dataCallback, // Sends back filtered data from react-table
  ...props
}) {
  const ref = React.useRef(null);
  const [filterOrSortUpdated, setFilterOrSortUpdated] = React.useState(0);
  React.useEffect(() => {
    if (ref.current) {
      const { sortedData } = ref.current.getResolvedState();
      const processedData = sortedData.map(d => d._original);
      dataCallback(processedData);
    } else {
      dataCallback(data);
    }
  }, [data, filterOrSortUpdated, dataCallback]);
  const noDataComponent = hideNoDataComponent
    ? () => <div />
    : () => (
        <UiEmptyState
          title={title}
          to={emptyTo}
          onClickButton={emptyOnClickButton}
          renderButton={renderButton}
          hideButton={hideButton}
        />
      );

  return (
    <div className="ui-table">
      <ReactTable
        ref={ref}
        minRows={!(data || []).length ? minRows : 0}
        showPagination={
          useTablePagination &&
          (data || []).length > (pageSize || DEFAULT_PAGE_SIZE)
        }
        defaultPageSize={
          useTablePagination ? pageSize || DEFAULT_PAGE_SIZE : undefined
        }
        data={data}
        onSortedChange={(newSorted, column, shiftKey) => {
          paging.sort(newSorted, column, shiftKey);
          setFilterOrSortUpdated(filterOrSortUpdated + 1);
        }}
        onFilteredChange={() => {
          setFilterOrSortUpdated(filterOrSortUpdated + 1);
        }}
        NoDataComponent={noDataComponent}
        loading={loading}
        {...props}
      />
      <Columns isGapless>
        {!hidePrev && (
          <Column isSize="narrow">
            <NextButton to={toPrev} onClick={onClickPrev} isPrev>
              Previous
            </NextButton>
          </Column>
        )}
        <Column>
          <UiPagination
            currentPage={paging.getPaging().page - 1}
            itemsPerPage={paging.getPaging().pageSize}
            onNextPage={paging.next}
            onPrevPage={paging.previous}
            totalItems={paging.getPaging().totalResults}
            onGoToPage={paging.goToPage}
          />
        </Column>
        {!hideNext && (
          <Column isSize="narrow">
            <NextButton to={toNext} onClick={onClickNext} />
          </Column>
        )}
      </Columns>
    </div>
  );
}

UiTable.defaultProps = {
  paging: {
    getPaging: () => ({
      page: 1,
      pageSize: 10,
      totalResults: 0,
    }),
    next: _.noop,
    previous: _.noop,
    sort: _.noop,
    goToPage: _.noop,
  },
  useTablePagination: false,
  emptyTo: '',
  emptyOnClickButton: null,
  renderButton: null,
  hideButton: false,
  loading: false,
  onClickNext: null,
  toNext: null,
  onClickPrev: null,
  toPrev: null,
  hidePrev: false,
  hideNext: false,
  hideNoDataComponent: false,
  minRows: 0,
  dataCallback: _.noop,
};

UiTable.propTypes = {
  data: propTypes.arrayOf(propTypes.object).isRequired,
  paging: propTypes.object,
  useTablePagination: propTypes.bool,
  title: propTypes.string.isRequired,
  emptyTo: propTypes.string,
  emptyOnClickButton: propTypes.func,
  renderButton: propTypes.node,
  hideButton: propTypes.bool,
  loading: propTypes.bool,
  hideNext: propTypes.bool,
  hidePrev: propTypes.bool,
  toNext: propTypes.string,
  onClickNext: propTypes.func,
  toPrev: propTypes.string,
  onClickPrev: propTypes.func,
  hideNoDataComponent: propTypes.bool,
  minRows: propTypes.number,
  dataCallback: propTypes.func,
  ...ReactTable.propTypes,
};
